/**
 * @fileoverview Centralized logging utility for TowApp
 *
 * Provides environment-aware logging that only outputs in development mode.
 * This prevents debug information from appearing in production builds while
 * maintaining helpful logging during development.
 *
 * @example
 * ```typescript
 * import { logger } from '../lib/logger';
 *
 * logger.info('User logged in', { userId: user.id });
 * logger.error('Failed to fetch data', error);
 * logger.debug('Zone calculation', { zones, result });
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

/**
 * Determines if the application is running in development mode
 */
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Determines if debug mode is explicitly enabled via environment variable
 */
const isDebugEnabled = process.env.REACT_APP_DEBUG_MODE === 'true';

/**
 * Formats a log message with optional context data
 */
const formatMessage = (level: LogLevel, message: string, context?: LogContext): string => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
  return `${prefix} ${message}`;
};

/**
 * Centralized logger for TowApp
 *
 * Features:
 * - Automatically disabled in production builds
 * - Supports structured logging with context objects
 * - Color-coded output in development
 * - Can be enabled in production via REACT_APP_DEBUG_MODE
 */
export const logger = {
  /**
   * Logs debug-level messages (only in development or when debug mode is enabled)
   * Use for detailed diagnostic information during development
   */
  debug: (message: string, context?: LogContext): void => {
    if (isDevelopment || isDebugEnabled) {
      console.debug(formatMessage('debug', message), context ?? '');
    }
  },

  /**
   * Logs info-level messages
   * Use for general operational information
   */
  info: (message: string, context?: LogContext): void => {
    if (isDevelopment || isDebugEnabled) {
      console.info(formatMessage('info', message), context ?? '');
    }
  },

  /**
   * Logs warning-level messages
   * Use for potentially problematic situations
   */
  warn: (message: string, context?: LogContext): void => {
    if (isDevelopment || isDebugEnabled) {
      console.warn(formatMessage('warn', message), context ?? '');
    }
  },

  /**
   * Logs error-level messages (always enabled, even in production)
   * Use for error conditions that should always be logged
   */
  error: (message: string, error?: unknown, context?: LogContext): void => {
    // Errors are always logged, even in production
    console.error(formatMessage('error', message), error ?? '', context ?? '');
  },

  /**
   * Creates a namespaced logger for a specific module
   * Useful for filtering logs by component/service
   *
   * @example
   * ```typescript
   * const log = logger.createNamespace('WazeService');
   * log.info('Fetching data'); // [INFO] [WazeService] Fetching data
   * ```
   */
  createNamespace: (namespace: string) => ({
    debug: (message: string, context?: LogContext) =>
      logger.debug(`[${namespace}] ${message}`, context),
    info: (message: string, context?: LogContext) =>
      logger.info(`[${namespace}] ${message}`, context),
    warn: (message: string, context?: LogContext) =>
      logger.warn(`[${namespace}] ${message}`, context),
    error: (message: string, error?: unknown, context?: LogContext) =>
      logger.error(`[${namespace}] ${message}`, error, context),
  }),
};

export default logger;

